import type { UIMessage } from 'ai';
import type { ConfidenceLevel, NutritionSourceKind } from '@calorie-tracker/nutrition';

export type NutritionLogMessage = UIMessage<
  {
    confidence?: ConfidenceLevel;
    source?: NutritionSourceKind;
  },
  never
>;

export const nutritionLogAttachmentKinds = ['audio', 'file', 'image'] as const;

export type NutritionLogAttachmentKind = (typeof nutritionLogAttachmentKinds)[number];

export interface NutritionLogAttachmentImageUpload {
  bucketName?: string;
  objectKey: string;
  purpose: 'food-label' | 'meal-photo';
  status: 'uploaded' | 'processed' | 'failed';
  uploadId: string;
}

export interface NutritionLogAttachmentInput {
  contentType: string;
  imageUpload?: NutritionLogAttachmentImageUpload;
  name: string;
  size: number;
}

export interface NutritionLogAttachment extends NutritionLogAttachmentInput {
  id: string;
  kind: NutritionLogAttachmentKind;
}

export const nutritionLogMessageRoles = ['assistant', 'user'] as const;

export type NutritionLogMessageRole = (typeof nutritionLogMessageRoles)[number];

export const nutritionLogToolNames = [
  'calculate-expression',
  'calculate-nutrition',
  'create-draft-suggestion',
  'get-logged-meal-history',
  'get-usda-food-details',
  'normalize-draft-entry',
  'search-food-library',
  'search-usda-foods',
  'web-search',
] as const;

export type NutritionLogToolName = (typeof nutritionLogToolNames)[number];

export const nutritionLogToolStatuses = ['running', 'completed', 'failed'] as const;

export type NutritionLogToolStatus = (typeof nutritionLogToolStatuses)[number];

export interface NutritionLogToolActivity {
  detail?: string;
  id: string;
  label: string;
  status: NutritionLogToolStatus;
  toolName: NutritionLogToolName;
  updatedAt: string;
}

export const nutritionLogMessageStatuses = ['complete', 'error', 'streaming'] as const;

export type NutritionLogMessageStatus = (typeof nutritionLogMessageStatuses)[number];

export interface NutritionLogSessionMessage {
  attachments: NutritionLogAttachment[];
  createdAt: string;
  id: string;
  role: NutritionLogMessageRole;
  status: NutritionLogMessageStatus;
  text: string;
  toolActivities: NutritionLogToolActivity[];
}

export interface NutritionLogSession {
  createdAt: string;
  id: string;
  messages: NutritionLogSessionMessage[];
  title: string;
  updatedAt: string;
}

function trimMessageText(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function createIsoTimestamp(now: Date): string {
  return now.toISOString();
}

function inferAttachmentKind(contentType: string): NutritionLogAttachmentKind {
  if (contentType.startsWith('image/')) {
    return 'image';
  }

  if (contentType.startsWith('audio/')) {
    return 'audio';
  }

  return 'file';
}

function deriveSessionTitle(message: NutritionLogSessionMessage): string {
  if (message.text.length > 0) {
    return message.text.length > 32 ? `${message.text.slice(0, 32).trimEnd()}…` : message.text;
  }

  if (message.attachments.length > 0) {
    return message.attachments[0]?.name ?? 'New log';
  }

  return 'New log';
}

export function createNutritionLogAttachment(
  input: NutritionLogAttachmentInput,
): NutritionLogAttachment {
  return {
    id: crypto.randomUUID(),
    kind: inferAttachmentKind(input.contentType),
    name: input.name,
    size: input.size,
    contentType: input.contentType,
    ...(input.imageUpload ? { imageUpload: input.imageUpload } : {}),
  };
}

export function createNutritionLogSession(now: Date = new Date()): NutritionLogSession {
  const timestamp = createIsoTimestamp(now);

  return {
    id: crypto.randomUUID(),
    title: 'New log',
    createdAt: timestamp,
    updatedAt: timestamp,
    messages: [],
  };
}

export function createNutritionLogSessionMessage(input: {
  attachments?: NutritionLogAttachment[];
  now?: Date;
  role: NutritionLogMessageRole;
  text: string;
}): NutritionLogSessionMessage {
  const timestamp = createIsoTimestamp(input.now ?? new Date());

  return {
    id: crypto.randomUUID(),
    role: input.role,
    text: trimMessageText(input.text),
    attachments: input.attachments ?? [],
    createdAt: timestamp,
    status: 'complete',
    toolActivities: [],
  };
}

export function appendMessageToNutritionLogSession(
  session: NutritionLogSession,
  message: NutritionLogSessionMessage,
): NutritionLogSession {
  const messages = [...session.messages, message];
  const userMessages = messages.filter((entry) => entry.role === 'user');
  const title = userMessages[0] ? deriveSessionTitle(userMessages[0]) : session.title;

  return {
    ...session,
    title,
    updatedAt: message.createdAt,
    messages,
  };
}

export const nutritionExtractionSystemPrompt = [
  'You assist with food logging.',
  'Preserve original user input.',
  'Return structured nutrition drafts, never final writes.',
  'Prefer USDA-backed values when available.',
  'Flag uncertain values for review instead of fabricating certainty.',
].join(' ');
